import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';

const api = apiService();
const formApi = apiService('form');

// Async actions

// Create a new Gamic (upload files + metadata)
export const addGamic = createAsyncThunk(
  'gamics/addGamic',
  async (gamicData: FormData) => {
    console.log('Sending Request');
    console.log(gamicData); // logs the FormData object itself

    // To see each key/value pair:
    for (const [key, value] of gamicData.entries()) {
      console.log(key, value);
    }

    const response = await formApi.post('/gamic/', gamicData);

    console.log(response);
    return response.data;
  },
);

// Fetch all Gamics for mobile (with optional filters)
export const fetchGamics = createAsyncThunk(
  'gamics/fetchGamics',
  async (
    params: {
      page?: number;
      limit?: number;
      userId?: string;
      name?: string;
    } = {},
  ) => {
    const response = await api.get('/gamic', { params });
    return response.data;
  },
);

// Fetch current user’s Gamics (with optional filters)
export const fetchMyGamics = createAsyncThunk(
  'gamics/fetchMyGamics',
  async (
    params: {
      page?: number;
      limit?: number;
      title?: string;
      status?: string;
      categoryName?: string;
    } = {},
  ) => {
    const response = await api.get('/gamic/me', { params });
    return response.data;
  },
);

export const fetchGamicById = createAsyncThunk(
  'gamics/fetchGamicById',
  async (id: string) => {
    const response = await api.get(`/gamic/${id}`);
    return response.data;
  },
);

// Fetch analytics for current user’s Gamics
export const fetchMyGamicsAnalytics = createAsyncThunk(
  'gamics/fetchMyGamicsAnalytics',
  async ({
    timeRange,
  }: {
    timeRange: 'daily' | 'monthly' | 'yearly' | 'weekly';
  }) => {
    const response = await api.get('/gamic/analytics/me', {
      params: { timeRange },
    });
    return response.data;
  },
);

// Fetch analytics for all Gamics (admin)
export const fetchAllGamicsAnalytics = createAsyncThunk(
  'gamics/fetchAllGamicsAnalytics',
  async ({
    timeRange,
  }: {
    timeRange: 'daily' | 'monthly' | 'yearly' | 'weekly';
  }) => {
    const response = await api.get('/gamic/analytics', {
      params: { timeRange },
    });
    return response.data;
  },
);

// Fetch all Gamics for admin (with optional filters)
export const fetchAllGamicsAdmin = createAsyncThunk(
  'gamics/fetchAllGamicsAdmin',
  async (
    params: {
      title?: string;
      category?: string;
      status?: string;
      limit?: number;
      page?: number; // ✅ now TS knows about it
    } = {},
  ) => {
    const response = await api.get('/gamic/all', { params });
    return response.data; // { data: [...], pagination: {...} }
  },
);

// Update a Gamic (metadata or files)
export const updateGamic = createAsyncThunk(
  'gamics/updateGamic',
  async ({ id, formData }: { id: string; formData: FormData }) => {
    const response = await api.patch(`/gamic/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
);

// WL-DL-V1: admin-only thunk to edit a gamic's iOS/Android store URLs
// without resetting status to pending. Backend triggers the wishlist
// download-live notification helper.
export const updateGamicLinks = createAsyncThunk(
  'gamics/updateGamicLinks',
  async ({
    gamicId,
    iosDownloadUrl,
    androidDownloadUrl,
  }: {
    gamicId: string;
    iosDownloadUrl?: string;
    androidDownloadUrl?: string;
  }) => {
    const response = await api.patch(`/gamic/links/${gamicId}`, {
      iosDownloadUrl,
      androidDownloadUrl,
    });
    if (response.status !== 200) {
      toast.error('Failed to update download links');
    } else {
      toast.success('Download links updated');
    }
    return response.data;
  },
);

// Update Gamic status (approve/rejected + tags)
export const updateGamicStatus = createAsyncThunk(
  'gamics/updateGamicStatus',
  async ({
    gamicId,
    status,
    tags,
  }: {
    gamicId: string;
    status: string;
    tags?: string[];
  }) => {
    const response = await api.patch(`/gamic/status/${gamicId}`, {
      status,
      tags,
    });

    if (response.status !== 200) {
      toast.error('Failed to update gamic status');
    } else {
      toast.success('Gamic status updated successfully');
    }
    return response.data;
  },
);

// Delete a Gamic
export const deleteGamic = createAsyncThunk(
  'gamics/deleteGamic',
  async (gamicId: string) => {
    await api.delete(`/gamic/delete-gamics/${gamicId}`);
    return gamicId;
  },
);

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
}

interface GamicState {
  /* … */
  adminPagination?: PaginationInfo;
}

// Slice
interface GamicState {
  list: any[];
  myList: any[];
  adminList: any[];
  analytics: any;
  adminStats: {
    // 👈 NEW
    total: number;
    approved: number;
    rejected: number;
    pending: number;
  } | null;
  myAnalytics: any;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: GamicState = {
  list: [],
  myList: [],
  adminList: [],
  analytics: null,
  myAnalytics: null,
  status: 'idle',
  error: null,
  adminPagination: undefined,
  adminStats: null,
};

const gamicSlice = createSlice({
  name: 'gamics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Add Gamic
      .addCase(addGamic.fulfilled, (state, action) => {
        state.list.push(action.payload);
      })

      // Fetch Gamics (mobile)
      .addCase(fetchGamics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchGamics.fulfilled, (state, action) => {
        state.list = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchGamics.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })

      // Fetch My Gamics
      .addCase(fetchMyGamics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMyGamics.fulfilled, (state, action) => {
        state.myList = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchMyGamics.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })

      // Fetch My Gamics
      .addCase(fetchGamicById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchGamicById.fulfilled, (state, action) => {
        state.myList = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchGamicById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })

      // Fetch My Gamics Analytics
      .addCase(fetchMyGamicsAnalytics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMyGamicsAnalytics.fulfilled, (state, action) => {
        state.myAnalytics = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchMyGamicsAnalytics.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })

      // Fetch All Gamics Analytics (admin)
      .addCase(fetchAllGamicsAnalytics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAllGamicsAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchAllGamicsAnalytics.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })

      // Fetch All Gamics for Admin
      .addCase(fetchAllGamicsAdmin.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAllGamicsAdmin.fulfilled, (state, action) => {
        const { data, pagination, stats } = action.payload; // ← include stats

        const requestedPage = action.meta.arg?.page ?? 1;
        state.adminList =
          requestedPage > 1 ? [...state.adminList, ...data] : data;

        state.adminPagination = pagination ?? null;
        state.adminStats = stats ?? null; // ← store it
        state.status = 'succeeded';
      })

      // Update Gamic
      .addCase(updateGamic.fulfilled, (state, action) => {
        const idx = state.list.findIndex((g) => g.id === action.payload.id);
        if (idx !== -1) {
          state.list[idx] = action.payload;
        }
      })

      // Update Gamic Status
      .addCase(updateGamicStatus.fulfilled, (state, action) => {
        const idx = state.list.findIndex((g) => g.id === action.payload.id);
        if (idx !== -1) {
          state.list[idx] = action.payload;
        }
      })

      // Delete Gamic
      .addCase(deleteGamic.fulfilled, (state, action) => {
        state.list = state.list.filter((g) => g.id !== action.payload);
      });
  },
});

export default gamicSlice.reducer;
